import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { api, asset, formatUSD } from "../lib/api";

const computePrice = (cat, size, media, finish, framing, qty) => {
  if (!cat) return null;
  const base = cat.sizes[size]?.base || 0;
  let unit = base * (cat.media[media]?.mult || 1) * (cat.finishes[finish]?.mult || 1);
  unit += base * (cat.framing[framing]?.add_mult || 0);
  unit = Math.round(unit * 100) / 100;
  const subtotal = Math.round(unit * qty * 100) / 100;
  const shipping = subtotal >= cat.free_ship_over ? 0 : cat.shipping_flat;
  const tax = Math.round(subtotal * cat.tax_rate * 100) / 100;
  const total = Math.round((subtotal + shipping + tax) * 100) / 100;
  return { unit, subtotal, shipping, tax, total };
};

export const PrintOrderModal = ({ open, onClose, catalog, context }) => {
  const [size, setSize] = useState("16x20");
  const [media, setMedia] = useState("fine_art_paper");
  const [finish, setFinish] = useState("matte");
  const [framing, setFraming] = useState("none");
  const [qty, setQty] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [addr, setAddr] = useState({ address1: "", city: "", state: "", postal_code: "", country: "US" });
  const [loading, setLoading] = useState(false);

  const price = useMemo(() => computePrice(catalog, size, media, finish, framing, qty), [catalog, size, media, finish, framing, qty]);
  if (!context) return null;
  const preview = context.dataUrl || asset(context.imageUrl);

  const submit = async () => {
    if (!name || !email || !addr.address1 || !addr.city) {
      toast.error("Please fill in your name, email and shipping address.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        type: context.kind === "frame" ? "frame_print" : "photo_print",
        timelapse_id: context.timelapseId || null,
        photo_id: context.photoId || null,
        frame_index: context.frameIndex ?? null,
        frame_timestamp: context.timestamp || null,
        image_data_url: context.dataUrl || null,
        source_image_url: context.kind === "photo" ? context.imageUrl : null,
        source_title: context.title,
        size, media, finish, framing, quantity: qty,
        customer_name: name, customer_email: email,
        shipping_address: addr,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/orders/print", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="print-order-modal" className="max-w-4xl w-[95vw] max-h-[92vh] overflow-y-auto bg-obsidian-800 border-white/10 p-0">
        <DialogTitle className="sr-only">Order a print</DialogTitle>
        <DialogDescription className="sr-only">Choose size, medium and finish, enter shipping details and proceed to secure checkout.</DialogDescription>
        <div className="grid md:grid-cols-2">
          {/* preview */}
          <div className="bg-black p-6 flex flex-col">
            <div className="overline-label mb-3">Miller's Professional Imaging</div>
            <div className="flex-1 flex items-center justify-center min-h-[280px]">
              {preview ? (
                <img data-testid="print-preview-img" src={preview} alt={context.title}
                  className="max-h-[52vh] w-auto object-contain shadow-2xl border border-white/10" />
              ) : <div className="text-neutral-500 text-sm">No preview available</div>}
            </div>
            <div className="mt-4">
              <div className="font-serif text-2xl">{context.title}</div>
              {context.timestamp && <div className="font-mono text-xs text-amber-glow mt-1">{context.timestamp}
                {context.frameIndex != null && ` · frame ${context.frameIndex + 1}`}</div>}
            </div>
          </div>

          {/* options */}
          <div className="p-6 space-y-5">
            <div>
              <h3 className="font-serif text-3xl font-light">Order a print</h3>
              <p className="text-neutral-400 text-sm mt-1">Museum-grade, made to order.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Size">
                <Select value={size} onValueChange={setSize}>
                  <SelectTrigger data-testid="print-size-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(catalog?.sizes || {}).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label} — {formatUSD(v.base)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Medium">
                <Select value={media} onValueChange={setMedia}>
                  <SelectTrigger data-testid="print-media-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(catalog?.media || {}).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Finish">
                <Select value={finish} onValueChange={setFinish}>
                  <SelectTrigger data-testid="print-finish-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(catalog?.finishes || {}).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Framing">
                <Select value={framing} onValueChange={setFraming}>
                  <SelectTrigger data-testid="print-framing-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(catalog?.framing || {}).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <Field label="Quantity">
              <div className="flex items-center gap-3">
                <button className="w-9 h-9 border border-white/15 rounded hover:border-amber-glow" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus">−</button>
                <span data-testid="qty-value" className="w-8 text-center font-mono">{qty}</span>
                <button className="w-9 h-9 border border-white/15 rounded hover:border-amber-glow" onClick={() => setQty(qty + 1)} data-testid="qty-plus">+</button>
              </div>
            </Field>

            <div className="border-t border-white/10 pt-4 grid grid-cols-2 gap-3">
              <Input data-testid="ship-name" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
              <Input data-testid="ship-email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <Input data-testid="ship-address" className="col-span-2" placeholder="Street address" value={addr.address1} onChange={(e) => setAddr({ ...addr, address1: e.target.value })} />
              <Input data-testid="ship-city" placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
              <Input data-testid="ship-state" placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} />
              <Input data-testid="ship-zip" placeholder="ZIP / Postal" value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} />
              <Input data-testid="ship-country" placeholder="Country" value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} />
            </div>

            {price && (
              <div className="border-t border-white/10 pt-4 space-y-1 text-sm">
                <Row label={`Subtotal (${qty})`} value={formatUSD(price.subtotal)} />
                <Row label="Shipping" value={price.shipping === 0 ? "Free" : formatUSD(price.shipping)} />
                <Row label="Tax" value={formatUSD(price.tax)} />
                <div className="flex justify-between pt-2 text-lg">
                  <span className="font-serif">Total</span>
                  <span data-testid="print-total" className="font-mono text-amber-glow">{formatUSD(price.total)}</span>
                </div>
              </div>
            )}

            <button data-testid="print-checkout-btn" onClick={submit} disabled={loading}
              className="w-full h-12 rounded-full btn-amber flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={16} />}
              {loading ? "Redirecting…" : "Proceed to secure checkout"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="overline-label block mb-1.5">{label}</span>
    {children}
  </label>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between text-neutral-400"><span>{label}</span><span className="text-neutral-200">{value}</span></div>
);
